import { useState, useCallback, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import { getDiary, submitDiary } from "../../../services/WeeklyDiaryService";
import { handleApiError } from "../../../utils/errorHandler";
import { normalizeDiary } from "../../WeeklyDiary/utils/diaryUtils";
import { getCurrentUserId, getSuccessMessage, getWeekRange, toApiDate } from "../utils/lessonPlanUtils";

const EMPTY_DIARY = {
  lessons: [],
  summary: { total: 0, completed: 0, pending: 0, flagged: 0 },
  reflection: "",
  isSubmitted: false,
  isApproved: false,
};

const toWeekStart = (weekStart) => toApiDate(getWeekRange(weekStart).start);

/** The logged-in teacher's diary for a week (all subjects): load it and submit the reflection. */
export const useWeeklyReview = () => {
  const [diary, setDiary] = useState(EMPTY_DIARY);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requestRef = useRef(0);

  const fetchDiary = useCallback(async (weekStart) => {
    const requestId = ++requestRef.current;
    setDiary(EMPTY_DIARY);

    setIsLoading(true);
    try {
      const response = await getDiary({
        teacher_id: getCurrentUserId(),
        week_start: toWeekStart(weekStart),
      });
      if (requestId === requestRef.current) setDiary(normalizeDiary(response));
    } catch (error) {
      if (requestId === requestRef.current) handleApiError(error);
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, []);

  const submitWeek = useCallback(async (weekStart, reflection, subjectId) => {
    setIsSubmitting(true);
    try {
      const response = await submitDiary({
        // Only when the diary API names one; the week's diary isn't tied to a subject
        ...(subjectId ? { subject_id: Number(subjectId) } : {}),
        week_start: toWeekStart(weekStart),
        reflection: reflection.trim(),
      });
      enqueueSnackbar(getSuccessMessage(response, "Weekly diary submitted"), { variant: "success" });
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { diary, isLoading, isSubmitting, fetchDiary, submitWeek };
};
